import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { initializeFirebase } from '../firebase/config';
import { createAuthService } from '../services/authService';
import metaPixelService from '../services/metaPixelService';
import firebaseAnalyticsService from '../services/firebaseAnalyticsService';
import { META_PIXEL_CONFIG, getEventConfig, CUSTOM_EVENTS } from '../config/metaPixelConfig';
import CalendlyModal from '../components/CalendlyModal';
import DemoTransferencias from '../components/DemoTransferencias';
import RegistroHeader from '../components/RegistroHeader';
import './Registro.css';

const PUNTOS_REGISTRO = [
    ['7 días gratis.', 'Sin tarjeta de crédito.'],
    ['Listo en 10 minutos.', 'Conectás tu Mercado Pago y sumás a tu equipo.'],
    ['Sin riesgos.', 'No ven el saldo ni pueden mover plata.'],
];

function RegisterData() {
    const [formData, setFormData] = useState({
        fullName: '',
        businessName: '',
        email: '',
        confirmEmail: '',
        howDidYouKnowUs: ''
    });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [formProgress, setFormProgress] = useState(0);
    const [pixelInitialized, setPixelInitialized] = useState(false);
    const [pageLoadTime] = useState(Date.now()); // Para medir tiempo en página
    const [showCalendlyModal, setShowCalendlyModal] = useState(false);
    const formularioIniciado = useRef(false);
    const navigate = useNavigate();

    // Trackear abandono de página. pagehide y no beforeunload: en el celular es el que
    // se dispara de verdad.
    useEffect(() => {
        const handlePageHide = () => {
            const completedFields = [
                formData.fullName,
                formData.businessName,
                formData.email,
                formData.confirmEmail,
                formData.howDidYouKnowUs
            ].filter(field => field && field.trim() !== '').length;

            if (completedFields > 0 && completedFields < 5) {
                // Usuario abandonó con formulario parcialmente completo
                firebaseAnalyticsService.trackFormAbandonment(
                    'register_data',
                    completedFields,
                    5,
                    Date.now() - pageLoadTime
                );
            }

            // Trackear tiempo total en página
            firebaseAnalyticsService.trackPageTiming(
                'register_data',
                Date.now() - pageLoadTime,
                completedFields
            );
        };

        window.addEventListener('pagehide', handlePageHide);
        return () => {
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, [formData, pageLoadTime]);

    // Inicializar Meta Pixel y Firebase Analytics cuando se carga la página
    useEffect(() => {
        if (!pixelInitialized) {
            // Inicializar Meta Pixel
            metaPixelService.initializePixel(META_PIXEL_CONFIG.pixelId);

            // Inicializa Firebase Analytics y registra que entró al registro
            firebaseAnalyticsService.trackRegistro('registro_inicio');

            // Trackear que el usuario está viendo el contenido de registro
            const viewContentConfig = getEventConfig('viewContent', 'registrationStep1');
            metaPixelService.trackViewContent({
                ...viewContentConfig,
                form_step: 'register_data'
            });

            setPixelInitialized(true);

            console.log('Meta Pixel y Firebase Analytics inicializados en RegisterData');
        }
    }, [pixelInitialized]);

    // Trackear progreso del formulario
    useEffect(() => {
        const totalFields = 5; // fullName, businessName, email, confirmEmail, howDidYouKnowUs
        const completedFields = [
            formData.fullName,
            formData.businessName,
            formData.email,
            formData.confirmEmail,
            formData.howDidYouKnowUs
        ].filter(field => field && field.trim() !== '').length;

        const newProgress = Math.round((completedFields / totalFields) * 100);

        if (newProgress !== formProgress && newProgress > 0) {
            setFormProgress(newProgress);

            // Trackear progreso del formulario
            metaPixelService.trackFormProgress('register_data', completedFields, totalFields);
        }
    }, [formData, formProgress]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: value
        });
        setError(null);

        // ✅ OPTIMIZADO: Solo trackear cuando el usuario actualiza datos importantes
        // NO trackear cada tecla, solo cuando hay datos útiles

        // Actualizar datos del usuario para tracking
        if (name === 'email' && value) {
            metaPixelService.updateUserData({ email: value });
        }

        if (name === 'fullName' && value) {
            const nameParts = value.split(' ');
            metaPixelService.updateUserData({
                firstName: nameParts[0],
                lastName: nameParts.slice(1).join(' ') || '',
                fullName: value
            });
        }

        if (name === 'howDidYouKnowUs' && value) {
            metaPixelService.updateUserData({
                acquisition_source: value
            });
        }
    };

    // ✅ NUEVO: Trackear solo cuando el usuario ENTRA al campo
    const handleFieldFocus = (fieldName) => {
        // La primera vez que toca un campo: entró al registro y además empezó a completarlo.
        if (!formularioIniciado.current) {
            formularioIniciado.current = true;
            firebaseAnalyticsService.trackRegistro('registro_formulario_inicio');
        }

        metaPixelService.trackFormInteraction('register_data', 'field_focus', fieldName);
    };

    // ✅ NUEVO: Trackear solo cuando el usuario SALE del campo
    const handleFieldBlur = (fieldName, value) => {
        if (value && value.trim() !== '') {
            metaPixelService.trackFormInteraction('register_data', 'field_completed', fieldName);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.email !== formData.confirmEmail) {
            setError('Los correos electrónicos no coinciden');
            return;
        }

        // Trackear inicio del envío del formulario
        metaPixelService.trackFormInteraction('register_data', 'form_submit', 'submit_button');

        setLoading(true);
        try {
            // Inicializar Firebase con retry automático
            console.log('🔥 Iniciando proceso de registro...');
            await initializeFirebase();

            // Verificar si el email ya existe
            console.log('📊 Verificando si el usuario ya existe...');
            const authService = await createAuthService();
            const existe = await authService.usuarioExiste(formData.email);

            if (existe) {
                setError(
                    <div>
                        Usuario ya registrado, inicie sesión para acceder{' '}
                        <Link to="/login" className="login-link">Iniciar sesión</Link>
                    </div>
                );
                setLoading(false);
                firebaseAnalyticsService.trackRegistro('registro_error', { paso: 'datos', codigo: 'ya_registrado' });
                return;
            }

            // Guardar datos para uso posterior en localStorage
            const userData = {
                fullName: formData.fullName,
                businessName: formData.businessName,
                email: formData.email,
                howDidYouKnowUs: formData.howDidYouKnowUs,
                createdAt: new Date().toISOString()
            };

            localStorage.setItem('pendingUserData', JSON.stringify(userData));
            // Guardar una indicación para que la página de Negocio sepa que debe desplazarse hacia arriba
            localStorage.setItem('scrollToTop', 'true');
            console.log('Datos guardados en localStorage para uso posterior');

            // Trackear evento Lead - Usuario completó el primer paso del registro
            const leadConfig = getEventConfig('lead', 'stepCompleted');
            metaPixelService.trackLead({
                ...leadConfig,
                form_step: 'register_data'
            });

            // Trackear InitiateCheckout - Usuario avanza al siguiente paso
            const checkoutConfig = getEventConfig('initiateCheckout', 'stepAdvancement');
            metaPixelService.trackInitiateCheckout({
                ...checkoutConfig,
                form_step: 'register_data_to_business'
            });

            // Trackear en Firebase Analytics - Usuario completó paso 1
            firebaseAnalyticsService.trackRegistro('registro_paso1_completo', {
                como_nos_conocio: formData.howDidYouKnowUs,
                completion_time_seconds: Math.round((Date.now() - pageLoadTime) / 1000)
            });

            // Redirigir a la siguiente página del registro
            navigate('/registro/negocio');

        } catch (error) {
            console.error('Error en registro:', error);
            setError('Error en el registro. Por favor, intenta nuevamente.');

            // Trackear error en el formulario
            firebaseAnalyticsService.trackRegistro('registro_error', { paso: 'datos', codigo: 'error_inesperado' });
            metaPixelService.trackCustomEvent(CUSTOM_EVENTS.FORM_ERROR, {
                form_step: 'register_data',
                error_message: error.message,
                error_type: 'registration_error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Los cinco campos comparten estilo y tracking (Meta Pixel: entra al campo / lo completa).
    const campo = ({ name, label, type = 'text', placeholder, autoComplete, autoFocus = false }) => (
        <div className="registro-campo">
            <label htmlFor={`registro-${name}`} className="registro-label">{label}</label>
            <input
                id={`registro-${name}`}
                type={type}
                name={name}
                value={formData[name]}
                onChange={handleChange}
                onFocus={() => handleFieldFocus(name)}
                onBlur={(e) => handleFieldBlur(name, e.target.value)}
                className="registro-input"
                placeholder={placeholder}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                required
            />
        </div>
    );

    return (
        <div className="registro-pagina">
            <RegistroHeader onAgendar={() => setShowCalendlyModal(true)} />

            <main className="registro-pantalla">
                <div className="registro-tarjeta">
                    <section className="registro-form-lado">
                        <h1 className="registro-titulo">Creá tu cuenta</h1>
                        <p className="registro-bajada">7 días gratis, sin tarjeta. En 10 minutos lo tenés andando.</p>

                        <form onSubmit={handleSubmit} className="registro-formulario">
                            <div className="registro-fila">
                                {campo({ name: 'fullName', label: 'Nombre y apellido', placeholder: 'Ej.: Lucía Fernández', autoComplete: 'name', autoFocus: true })}
                                {campo({ name: 'businessName', label: 'Nombre del negocio', placeholder: 'Ej.: Café del Centro', autoComplete: 'organization' })}
                            </div>
                            <div className="registro-fila">
                                {campo({ name: 'email', label: 'Email', type: 'email', placeholder: 'tu@email.com', autoComplete: 'email' })}
                                {campo({ name: 'confirmEmail', label: 'Repetí tu email', type: 'email', placeholder: 'tu@email.com', autoComplete: 'email' })}
                            </div>

                            <div className="registro-campo">
                                <label htmlFor="registro-howDidYouKnowUs" className="registro-label">¿Cómo nos conociste?</label>
                                <select
                                    id="registro-howDidYouKnowUs"
                                    name="howDidYouKnowUs"
                                    value={formData.howDidYouKnowUs}
                                    onChange={handleChange}
                                    onFocus={() => handleFieldFocus('howDidYouKnowUs')}
                                    onBlur={(e) => handleFieldBlur('howDidYouKnowUs', e.target.value)}
                                    className="registro-input registro-select"
                                    required
                                >
                                    <option value="">Elegí una opción</option>
                                    <option value="Google">Google</option>
                                    <option value="Instagram">Instagram</option>
                                    <option value="Facebook">Facebook</option>
                                    <option value="Por recomendación">Por recomendación</option>
                                    <option value="Otra">Otra</option>
                                </select>
                            </div>

                            <button type="submit" className="registro-boton" disabled={loading}>
                                {loading && <span className="registro-spinner" aria-hidden="true" />}
                                {loading ? 'Procesando…' : 'Siguiente'}
                            </button>

                            {error && <div className="registro-error" role="alert">{error}</div>}
                        </form>

                        <p className="registro-ingresar">
                            ¿Ya tenés cuenta? <Link to="/login">Ingresá</Link>
                        </p>
                    </section>

                    <aside className="registro-info-lado" aria-hidden="true">
                        <DemoTransferencias
                            titulo="Tus empleados ven cada transferencia al instante"
                            resaltar="cada transferencia"
                            puntos={PUNTOS_REGISTRO}
                        />
                    </aside>
                </div>
            </main>

            <CalendlyModal
                isOpen={showCalendlyModal}
                onClose={() => setShowCalendlyModal(false)}
            />
        </div>
    );
}

export default RegisterData;
